import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, AlertCircle, Check, Download, FileSignature, CreditCard, Receipt, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AcceptAgreement() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [billing, setBilling] = useState("monthly");
  const [paymentMethod, setPaymentMethod] = useState("invoice");
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const fmt = (n, ccy) => `${(Number(n) || 0).toLocaleString("sv-SE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${ccy}`;

  useEffect(() => {
    axios.get(`${API}/agreement/${token}`)
      .then(r => {
        setData(r.data);
        setName(r.data.signed_by_name || "");
        setEmail(r.data.signed_by_email || r.data.customer?.email || "");
        if (r.data.signed_at) setAccepted(true);
      })
      .catch(() => setError("Avtalet kunde inte hittas"))
      .finally(() => setLoading(false));
  }, [token]);

  const hasRecurring = data?.items?.some(it => it.recurrence_type === "recurring");

  const handleAccept = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/agreement/${token}/accept`, {
        name: name.trim(),
        email: email.trim(),
        billing_interval_choice: billing,
        payment_method: paymentMethod,
      });
      const r = await axios.get(`${API}/agreement/${token}`);
      setData(r.data);
      setAccepted(true);
    } catch (err) {
      alert(err.response?.data?.detail || "Kunde inte signera. Försök igen.");
    }
    setSubmitting(false);
  };

  if (loading) return <div className="min-h-screen bg-[#F3F4F6] flex items-center justify-center"><Loader2 className="w-6 h-6 text-[#0F1F3A] animate-spin" /></div>;
  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#F3F4F6] flex items-center justify-center px-4">
        <div className="text-center">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <p className="text-[#0F1F3A] text-lg mb-1">{error || "Avtalet hittades inte"}</p>
          <p className="text-[#6B7280] text-sm">Kontrollera länken eller kontakta oss på hey@wedowedo.co</p>
        </div>
      </div>
    );
  }

  const cur = data.currency || "SEK";

  return (
    <div className="min-h-screen bg-[#F3F4F6] py-8 px-4" data-testid="accept-agreement">
      <div className="max-w-3xl mx-auto bg-white border border-[#E5E7EB] rounded-lg shadow-sm overflow-hidden">

        {/* Header */}
        <div className="px-8 py-6 border-b border-[#E5E7EB] bg-[#0F1F3A] text-white">
          <p className="text-[10px] font-mono tracking-wider text-[#9CA3AF] mb-1">AVTAL FÖR SIGNERING</p>
          <h1 className="font-outfit font-bold text-2xl mb-1">{data.title}</h1>
          {data.description && <p className="text-[#D1D5DB] text-sm leading-relaxed mt-2">{data.description}</p>}
        </div>

        {/* Parties */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 px-8 py-6 border-b border-[#E5E7EB]">
          <div>
            <p className="text-[10px] font-mono tracking-wider text-[#6B7280] mb-2">LEVERANTÖR</p>
            <p className="text-[#0F1F3A] text-sm font-semibold">WeDoWeDo OÜ</p>
            <p className="text-[#6B7280] text-xs leading-relaxed mt-1">Rannaniidu tn 2-1<br />13516 Tallinn, Estonia<br />Reg: 17455779 · VAT: EE102963621<br />hey@wedowedo.co</p>
          </div>
          <div>
            <p className="text-[10px] font-mono tracking-wider text-[#6B7280] mb-2">KUND</p>
            <p className="text-[#0F1F3A] text-sm font-semibold">{data.customer?.company_name}</p>
            <p className="text-[#6B7280] text-xs leading-relaxed mt-1">
              {data.customer?.contact_name && <>{data.customer.contact_name}<br /></>}
              {data.customer?.email && <>{data.customer.email}<br /></>}
              {data.customer?.org_number && <>Org: {data.customer.org_number}<br /></>}
              {data.customer?.vat_number && <>VAT: {data.customer.vat_number}</>}
            </p>
          </div>
        </div>

        {/* Items */}
        {(data.items || []).length > 0 && (
          <div className="px-8 py-6 border-b border-[#E5E7EB]">
            <p className="text-[10px] font-mono tracking-wider text-[#6B7280] mb-4">AVTALSRADER</p>
            <div className="space-y-3">
              {data.items.map((it, i) => (
                <div key={i} className="flex items-start justify-between gap-4 pb-3 border-b border-[#F3F4F6] last:border-0" data-testid={`item-${i}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-[#0F1F3A] text-sm font-medium">{it.title}</p>
                      <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm ${it.recurrence_type === "recurring" ? "bg-[#F59073]/10 text-[#F59073] border border-[#F59073]/30" : "bg-[#0F1F3A]/5 text-[#0F1F3A] border border-[#E5E7EB]"}`}>
                        {it.recurrence_type === "recurring" ? `LÖPANDE · ${it.billing_interval || "monthly"}` : "ENGÅNG"}
                      </span>
                    </div>
                    {it.description && <p className="text-[#6B7280] text-xs mt-1 leading-relaxed">{it.description}</p>}
                  </div>
                  <div className="text-right whitespace-nowrap">
                    <p className="text-[#0F1F3A] text-sm font-mono">{fmt((it.quantity || 1) * (it.unit_price || 0), cur)}</p>
                    {(it.quantity || 1) !== 1 && <p className="text-[#9CA3AF] text-[10px]">{it.quantity} × {fmt(it.unit_price || 0, cur)}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Totals */}
        <div className="px-8 py-6 bg-[#F9FAFB] border-b border-[#E5E7EB]">
          <p className="text-[10px] font-mono tracking-wider text-[#6B7280] mb-3">SAMMANFATTNING</p>
          <div className="space-y-2 text-sm">
            {data.totals?.one_time_total > 0 && (
              <div className="flex justify-between"><span className="text-[#374151]">Engångsavgift (totalt)</span><span className="text-[#0F1F3A] font-mono font-medium">{fmt(data.totals.one_time_total, cur)}</span></div>
            )}
            {data.totals?.monthly_total > 0 && (
              <>
                <div className="flex justify-between"><span className="text-[#374151]">Löpande avgift (per månad)</span><span className="text-[#0F1F3A] font-mono font-medium">{fmt(data.totals.monthly_total, cur)}/mån</span></div>
                <div className="flex justify-between"><span className="text-[#374151]">Vid årsfakturering (10×, <span className="text-green-600 font-medium">2 mån rabatt</span>)</span><span className="text-green-600 font-mono font-medium">{fmt(data.totals.yearly_discounted_total, cur)}/år</span></div>
                <div className="flex justify-between text-xs pt-1"><span className="text-green-700">Du sparar med årsval</span><span className="text-green-700 font-mono font-medium">{fmt(data.totals.yearly_savings, cur)}</span></div>
              </>
            )}
          </div>
          <p className="text-[#9CA3AF] text-[10px] mt-3">Alla priser anges exkl. moms/VAT. Vid B2B-försäljning inom EU tillämpas omvänd skattskyldighet.</p>
        </div>

        {/* Terms */}
        {(data.binding_period || data.notice_period || data.start_date || data.end_date || data.notes) && (
          <div className="px-8 py-6 border-b border-[#E5E7EB]">
            <p className="text-[10px] font-mono tracking-wider text-[#6B7280] mb-3">VILLKOR</p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {data.start_date && <div><span className="text-[#6B7280] text-xs">Startdatum</span><p className="text-[#0F1F3A]">{data.start_date}</p></div>}
              {data.end_date && <div><span className="text-[#6B7280] text-xs">Slutdatum</span><p className="text-[#0F1F3A]">{data.end_date}</p></div>}
              {data.binding_period && <div><span className="text-[#6B7280] text-xs">Bindningstid</span><p className="text-[#0F1F3A]">{data.binding_period}</p></div>}
              {data.notice_period && <div><span className="text-[#6B7280] text-xs">Uppsägningstid</span><p className="text-[#0F1F3A]">{data.notice_period}</p></div>}
            </div>
            {data.notes && <p className="text-[#374151] text-sm mt-3 leading-relaxed whitespace-pre-line">{data.notes}</p>}
          </div>
        )}

        {/* Sign / signed */}
        {accepted ? (
          <div className="px-8 py-8 bg-green-50 border-t-2 border-green-500">
            <div className="flex items-start gap-3">
              <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-green-800 font-semibold text-base mb-1">Avtalet är signerat</p>
                <p className="text-green-700 text-sm">Signerat av <strong>{data.signed_by_name}</strong> {data.signed_at?.slice(0, 10)} kl. {data.signed_at?.slice(11, 16)} UTC.</p>
                {data.signed_by_email && <p className="text-green-700 text-xs mt-1">{data.signed_by_email}</p>}
                {data.billing_interval_choice && hasRecurring && (
                  <p className="text-green-700 text-xs mt-2">Betalintervall för löpande del: <strong>{data.billing_interval_choice === "yearly" ? "årsvis (med 2 mån rabatt)" : "månadsvis"}</strong></p>
                )}
                {data.payment_method && hasRecurring && (
                  <p className="text-green-700 text-xs">Betalmetod: <strong>{data.payment_method === "card" ? "Kort (autodragning)" : "Faktura"}</strong></p>
                )}
                <a href={`${API}/agreement/${token}/pdf`} target="_blank" rel="noopener noreferrer"
                  data-testid="download-signed-pdf"
                  className="inline-flex items-center gap-2 mt-4 bg-[#0F1F3A] text-white text-sm px-4 py-2 rounded-sm hover:bg-[#1a2f4f] transition-colors">
                  <Download className="w-4 h-4" /> Ladda ner signerat avtal (PDF)
                </a>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleAccept} className="px-8 py-6">
            <p className="text-[10px] font-mono tracking-wider text-[#6B7280] mb-4">SIGNERA DIGITALT</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-xs text-[#6B7280] block mb-1">Ditt fullständiga namn *</label>
                <input value={name} onChange={e => setName(e.target.value)} required data-testid="signer-name"
                  className="w-full bg-white border border-[#D1D5DB] text-[#0F1F3A] px-3 py-2 rounded-sm text-sm focus:border-[#0F1F3A] outline-none" />
              </div>
              <div>
                <label className="text-xs text-[#6B7280] block mb-1">E-post (för bekräftelse)</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="signer-email"
                  className="w-full bg-white border border-[#D1D5DB] text-[#0F1F3A] px-3 py-2 rounded-sm text-sm focus:border-[#0F1F3A] outline-none" />
              </div>
            </div>

            {hasRecurring && (
              <>
                <div className="mb-4">
                  <label className="text-xs text-[#6B7280] block mb-2">Betalintervall för löpande del</label>
                  <div className="grid grid-cols-2 gap-2">
                    <label className={`flex items-center gap-2 border rounded-sm p-3 cursor-pointer transition-colors ${billing === "monthly" ? "border-[#0F1F3A] bg-[#F9FAFB]" : "border-[#D1D5DB] hover:border-[#9CA3AF]"}`}>
                      <input type="radio" name="billing" value="monthly" checked={billing === "monthly"} onChange={() => setBilling("monthly")} className="accent-[#0F1F3A]" data-testid="billing-monthly" />
                      <div>
                        <p className="text-[#0F1F3A] text-sm font-medium">Månadsvis</p>
                        <p className="text-[#6B7280] text-xs">{fmt(data.totals?.monthly_total || 0, cur)}/mån</p>
                      </div>
                    </label>
                    <label className={`flex items-center gap-2 border rounded-sm p-3 cursor-pointer transition-colors ${billing === "yearly" ? "border-green-600 bg-green-50" : "border-[#D1D5DB] hover:border-[#9CA3AF]"}`}>
                      <input type="radio" name="billing" value="yearly" checked={billing === "yearly"} onChange={() => setBilling("yearly")} className="accent-green-600" data-testid="billing-yearly" />
                      <div>
                        <p className="text-[#0F1F3A] text-sm font-medium">Årsvis <span className="text-green-600 text-xs ml-1">SPARA {fmt(data.totals?.yearly_savings || 0, cur)}</span></p>
                        <p className="text-[#6B7280] text-xs">{fmt(data.totals?.yearly_discounted_total || 0, cur)}/år (10 mån)</p>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="mb-5">
                  <label className="text-xs text-[#6B7280] block mb-2">Betalmetod för löpande del</label>
                  <div className="grid grid-cols-2 gap-2">
                    <label className={`flex items-center gap-2 border rounded-sm p-3 cursor-pointer transition-colors ${paymentMethod === "card" ? "border-[#0F1F3A] bg-[#F9FAFB]" : "border-[#D1D5DB] hover:border-[#9CA3AF]"}`}>
                      <input type="radio" name="pm" value="card" checked={paymentMethod === "card"} onChange={() => setPaymentMethod("card")} className="accent-[#0F1F3A]" data-testid="pm-card" />
                      <CreditCard className="w-4 h-4 text-[#374151]" />
                      <div><p className="text-[#0F1F3A] text-sm font-medium">Kort</p><p className="text-[#6B7280] text-xs">Autodragning</p></div>
                    </label>
                    <label className={`flex items-center gap-2 border rounded-sm p-3 cursor-pointer transition-colors ${paymentMethod === "invoice" ? "border-[#0F1F3A] bg-[#F9FAFB]" : "border-[#D1D5DB] hover:border-[#9CA3AF]"}`}>
                      <input type="radio" name="pm" value="invoice" checked={paymentMethod === "invoice"} onChange={() => setPaymentMethod("invoice")} className="accent-[#0F1F3A]" data-testid="pm-invoice" />
                      <Receipt className="w-4 h-4 text-[#374151]" />
                      <div><p className="text-[#0F1F3A] text-sm font-medium">Faktura</p><p className="text-[#6B7280] text-xs">30 dagar netto</p></div>
                    </label>
                  </div>
                </div>
              </>
            )}

            <div className="flex items-start gap-2 mb-5 text-xs text-[#6B7280] leading-relaxed">
              <ShieldCheck className="w-4 h-4 text-[#0F1F3A] flex-shrink-0 mt-0.5" />
              <p>Genom att klicka "Signera avtalet" bekräftar du att du har läst, förstått och godkänner avtalet ovan. Din signatur loggas digitalt med tidsstämpel och IP-adress.</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <a href={`${API}/agreement/${token}/pdf`} target="_blank" rel="noopener noreferrer"
                data-testid="download-pdf"
                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 bg-white border border-[#D1D5DB] text-[#374151] text-sm px-5 py-3 rounded-sm hover:border-[#9CA3AF] transition-colors">
                <Download className="w-4 h-4" /> Ladda ner som PDF
              </a>
              <button type="submit" disabled={submitting || !name.trim()} data-testid="sign-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#F59073] text-white text-sm font-semibold px-5 py-3 rounded-sm hover:bg-[#e8805f] transition-colors disabled:opacity-50">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />}
                {submitting ? "Signerar..." : "Signera avtalet"}
              </button>
            </div>
          </form>
        )}

        <div className="px-8 py-4 bg-[#F9FAFB] text-center text-[11px] text-[#9CA3AF]">
          WeDoWeDo OÜ &middot; hey@wedowedo.co &middot; wedowedo.co
        </div>
      </div>
    </div>
  );
}
